/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {TagInstance} from '../models/tag-instance';
import {User} from '../models/user';
import { UserProperty } from '../models/user-property';
import { ObjectInstance } from "../models/object-instance";
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';

@Injectable()
export class UserService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/users/`;
  }


  getAllUsers(): Promise<User[]> {
    const rootUrl = this.getUrl();
    return this.mRestService.get<User[]>(rootUrl);
  }

  getUsersFiltered(start: number, size: number, order: string, filterString: string): Promise<User[]> {
    const url = this.getUrl() + `filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=lastName&filterString=" + filterString;
    return this.mRestService.get<User[]>(url);
  } 

  getAllUsersForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<User>> {
    const rootUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC" + "&filterColumn=login&sortColumn=login&filterString=" + filterString;
    return this.mRestService.get<PagedResults<User>>(rootUrl);
  }

  getUserForId(id: number): Promise<User> {
    const rootUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<User>(rootUrl);
  }

  /* @deprecated */
  getTagsForUser(user: User): Promise<TagInstance[]> {
    const rootUrl = this.getUrl() + user.id + '/assigned_tags';
    return this.mRestService.get<TagInstance[]>(rootUrl);
  }

  getPersonForUser(userId: number): Promise<ObjectInstance> {
    const rootUrl = `${this.getUrl()}people/${userId}/assigned`;
      return this.mRestService.get<ObjectInstance>(rootUrl);
  }

  getAllUnassignedPeople(): Promise<ObjectInstance[]> {
    const rootUrl = `${this.getUrl()}people/unassigned`;
    return this.mRestService.get<ObjectInstance[]>(rootUrl);
  }

  validatePassword(userId: number, password: string): Promise<any> {
    const rootUrl = this.getUrl() + 'validate-password';

    const dto = {"user_id": userId, "password": password};

    return this.mRestService.post(rootUrl, dto)
      .then(response => {
        return null;
      })
  }

  createUser(user: User): Promise<User> {
    const rootUrl = this.getUrl();
    return this.mRestService.post<User>(rootUrl, user);
  }

  updateUser(user: User): Promise<User> {
    const rootUrl = this.getUrl();
    return this.mRestService.put<User>(rootUrl, user);

  }

  deleteUsers(ids: number[]): Promise<User> {
    const rootUrl = this.getUrl();
    return this.mRestService.delete<User>(rootUrl);

  }

  deleteUser(userId: number): Promise<User> {
    const rootUrl = this.getUrl();
    return this.mRestService.delete<User>(rootUrl + userId);

  }

  /** @deprecated */
  unassignAllTagsFromUser(user: User): Promise<any> {
    const rootUrl = `${this.getUrl()}${user.id}/assigned_tag`;
    return this.mRestService.delete(rootUrl);
  }

  /** @deprecated */
  assignTagsToUser(tags: TagInstance[], user: User): Promise<any> {
    const rootUrl = `${this.getUrl()}${user.id}/assigned_tags`;
    let result = null;
    for(const tag of tags) {
      result = this.mRestService.put(rootUrl, tag);
    }
    return result;
  }

  unassignPersonFromUser(userId: number): Promise<any> {
    const rootUrl = `${this.getUrl()}${userId}/people/unassign`;
    return this.mRestService.delete(rootUrl);
  }

  assignUserToPerson(userId: number, personId: number): Promise<any> {
    const rootUrl = `${this.getUrl()}${userId}/people/${personId}/assign`;
    return this.mRestService.post(rootUrl, "");
  }

  getAllUserProperties(): Promise<UserProperty[]> {
    const rootUrl = this.getUrl() + 'properties';
    return this.mRestService.get<UserProperty[]>(rootUrl);
  }

  getUserPropertyById(propertyId: number): Promise<UserProperty> {
    const rootUrl = this.getUrl() + `properties/${propertyId}`;
    return this.mRestService.get<UserProperty>(rootUrl);
  }

  updateUserProperty(property: UserProperty): Promise<UserProperty> {
    const rootUrl = this.getUrl() + 'properties';
    return this.mRestService.put<UserProperty>(rootUrl, property);
  }

  createUserProperty(property: UserProperty): Promise<UserProperty> {
    const rootUrl = this.getUrl() + 'properties';
    return this.mRestService.post<UserProperty>(rootUrl, property);
  }

  deleteUserPropertyById(propertyId: number): Promise<any> {
    const rootUrl = this.getUrl() + `properties/${propertyId}`;
    return this.mRestService.delete(rootUrl);
  }

  uploadUsers(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'data/upload';
    return this.mRestService.upload(callUrl, formData);
  }

  downloadUsers(): Promise<Blob> {
    const callUrl = this.getUrl() + 'data/download';

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob);
  }
}
